/**
 * Deterministic sample data for the dev mock backend.
 *
 * This is a *fixture generator*, not a second implementation of the Server: it
 * produces accounts, statements and transactions, and everything else is
 * derived from those in ./analysis.ts. That keeps recommendation evidence
 * pointing at transaction ids that actually exist, which is what the
 * recommendationDetail screen promises.
 */
import type { Account, CategoryId, Money, Statement, Transaction } from '@/generated/types'
import type { FeeKind } from '../api'

export type DatasetSize = 'full' | 'thin' | 'empty'

export const CURRENCY = 'USD'

const money = (amount: number): Money => ({ amount, currency: CURRENCY })

/** Stable grouping key for a merchant across accounts and statements. */
const merchantKey = (merchant: string) => merchant.toLowerCase().replace(/[^a-z0-9]+/g, '-')

/** Stable pseudo-uuid so ids survive a reload without a real database. */
export function uuid(seed: string): string {
  let h1 = 0x9e3779b9
  let h2 = 0x85ebca6b
  for (let i = 0; i < seed.length; i++) {
    h1 = Math.imul(h1 ^ seed.charCodeAt(i), 0x01000193) >>> 0
    h2 = Math.imul(h2 ^ (seed.charCodeAt(i) + i), 0x85ebca6b) >>> 0
  }
  const hex = (n: number, len: number) => n.toString(16).padStart(8, '0').slice(0, len)
  return [
    hex(h1, 8),
    hex(h2, 4),
    '4' + hex(h1 >>> 8, 3),
    ((h2 & 0x3) | 0x8).toString(16) + hex(h2 >>> 8, 3),
    hex(h1 ^ h2, 8) + hex(h1 + h2, 4),
  ].join('-')
}

function rng(seed: number) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const pad = (n: number) => String(n).padStart(2, '0')
const isoDate = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(Math.min(d, daysIn(y, m)))}`
const daysIn = (y: number, m: number) => new Date(y, m, 0).getDate()

export interface MonthSlot {
  key: string
  year: number
  month: number
  /** The current calendar month has partial coverage. */
  complete: boolean
}

/** Oldest first. `count` complete months, then the current partial month. */
function monthSlots(count: number, now: Date): MonthSlot[] {
  const slots: MonthSlot[] = []
  for (let i = count; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    slots.push({
      key: `${d.getFullYear()}-${pad(d.getMonth() + 1)}`,
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      complete: i > 0,
    })
  }
  return slots
}

interface AccountSeed {
  ref: string
  name: string
  institution: string
  type: Account['type']
  mask: string
  balance: number
  creditLimit?: number
  aprBasisPoints?: number | null
  minimumPayment?: number
}

const ACCOUNT_SEEDS: AccountSeed[] = [
  { ref: 'checking', name: 'Everyday Checking', institution: 'Meridian Bank', type: 'checking', mask: '1042', balance: 428_11 },
  { ref: 'savings', name: 'Emergency Savings', institution: 'Meridian Bank', type: 'savings', mask: '3390', balance: 6_412_00 },
  {
    ref: 'card',
    name: 'Sapphire Card',
    institution: 'Northgate',
    type: 'creditCard',
    mask: '8871',
    balance: -4_128_44,
    creditLimit: 9_000_00,
    aprBasisPoints: 2499,
    minimumPayment: 125_00,
  },
  {
    ref: 'card2',
    name: 'Blue Cash Card',
    institution: 'Cascade Credit',
    type: 'creditCard',
    mask: '2210',
    balance: -1_289_00,
    creditLimit: 4_000_00,
    aprBasisPoints: 1899,
    minimumPayment: 45_00,
  },
]

/** A charge that repeats on a cadence. Drives recurring detection downstream. */
interface RecurringSeed {
  merchant: string
  descriptor: string
  category: CategoryId
  account: string
  day: number
  amount: number
  /** Every N months. 1 = monthly, 12 = annual. */
  everyMonths?: number
  /** Amount changes at this offset (months from the first slot). */
  priceChange?: { atMonthIndex: number; amount: number }
  /** No engagement signal — feeds the cancelSubscription recommendation. */
  unused?: boolean
  duplicateOf?: string
}

const RECURRING_SEEDS: RecurringSeed[] = [
  { merchant: 'Netflix', descriptor: 'NETFLIX.COM 866-579-7172 CA', category: 'subscriptions', account: 'card', day: 8, amount: -15_49, priceChange: { atMonthIndex: 8, amount: -19_99 } },
  { merchant: 'Spotify', descriptor: 'SPOTIFY P0A1B2C3 NY', category: 'subscriptions', account: 'card', day: 3, amount: -11_99 },
  { merchant: 'Hulu', descriptor: 'HULU 877-8244858 CA', category: 'subscriptions', account: 'card', day: 12, amount: -17_99, duplicateOf: 'Netflix' },
  { merchant: 'Cloudline Storage', descriptor: 'CLOUDLINE STORAGE MONTHLY', category: 'subscriptions', account: 'card', day: 21, amount: -9_99, unused: true },
  { merchant: 'Ridgeline Gym', descriptor: 'RIDGELINE FITNESS MEMBER', category: 'personalCare', account: 'checking', day: 5, amount: -68_00, unused: true },
  { merchant: 'Harbor Insurance', descriptor: 'HARBOR INS PREM AUTOPAY', category: 'insurance', account: 'checking', day: 14, amount: -186_40 },
  { merchant: 'Citywide Power', descriptor: 'CITYWIDE POWER EBILL', category: 'utilities', account: 'checking', day: 18, amount: -132_75 },
  { merchant: 'Fiberline Internet', descriptor: 'FIBERLINE INTERNET AUTOPAY', category: 'utilities', account: 'checking', day: 22, amount: -84_99 },
  { merchant: 'Meadow Ridge Apartments', descriptor: 'MEADOW RIDGE APTS RENT', category: 'housing', account: 'checking', day: 1, amount: -1_845_00 },
  { merchant: 'The Ledger', descriptor: 'THE LEDGER DIGITAL SUB', category: 'subscriptions', account: 'card', day: 27, amount: -4_00 },
  { merchant: 'Atlas Design Suite', descriptor: 'ATLAS DESIGN ANNUAL PLAN', category: 'subscriptions', account: 'card', day: 16, amount: -239_88, everyMonths: 12 },
]

/**
 * What detection would learn from engagement signals the mock has no way to
 * see. Read by deriveRecurring() to set `unused` / `overlapsWith`.
 */
export const recurringHints = new Map(
  RECURRING_SEEDS.map((seed) => [
    merchantKey(seed.merchant),
    {
      unused: seed.unused === true,
      duplicateOf: seed.duplicateOf ? merchantKey(seed.duplicateOf) : null,
    },
  ]),
)

interface VariableSeed {
  merchant: string
  descriptor: string
  category: CategoryId
  account: string
  perMonth: [number, number]
  amount: [number, number]
}

const VARIABLE_SEEDS: VariableSeed[] = [
  { merchant: 'Greenhill Market', descriptor: 'GREENHILL MARKET #221', category: 'groceries', account: 'checking', perMonth: [3, 5], amount: [-142_00, -38_00] },
  { merchant: 'Corner Grocer', descriptor: 'CORNER GROCER POS', category: 'groceries', account: 'card', perMonth: [1, 3], amount: [-64_00, -16_00] },
  { merchant: 'Ember Kitchen', descriptor: 'EMBER KITCHEN 4471', category: 'dining', account: 'card', perMonth: [3, 7], amount: [-88_00, -14_00] },
  { merchant: 'Daybreak Coffee', descriptor: 'DAYBREAK COFFEE CO', category: 'dining', account: 'card', perMonth: [6, 12], amount: [-9_25, -4_15] },
  { merchant: 'Transit Authority', descriptor: 'METRO TRANSIT FARE', category: 'transport', account: 'checking', perMonth: [2, 4], amount: [-42_00, -20_00] },
  { merchant: 'Beacon Fuel', descriptor: 'BEACON FUEL #88', category: 'fuel', account: 'card', perMonth: [1, 3], amount: [-72_00, -34_00] },
  { merchant: 'Northgate Outfitters', descriptor: 'NORTHGATE OUTFITTERS', category: 'shopping', account: 'card', perMonth: [0, 3], amount: [-186_00, -22_00] },
  { merchant: 'Pinegrove Pharmacy', descriptor: 'PINEGROVE PHARMACY', category: 'healthcare', account: 'checking', perMonth: [0, 2], amount: [-64_00, -12_00] },
  { merchant: 'Lantern Cinema', descriptor: 'LANTERN CINEMA 12', category: 'entertainment', account: 'card2', perMonth: [0, 2], amount: [-38_00, -14_00] },
  { merchant: 'Rivet Hardware', descriptor: 'RIVET HARDWARE SUPPLY', category: 'shopping', account: 'card2', perMonth: [0, 2], amount: [-96_00, -18_00] },
]

interface FeeSeed {
  merchant: string
  descriptor: string
  account: string
  kind: FeeKind
  amount: number
  /** Month offsets (from the oldest slot) the fee lands in. */
  months: number[]
  day: number
}

const FEE_SEEDS: FeeSeed[] = [
  { merchant: 'Overdraft fee', descriptor: 'OVERDRAFT ITEM FEE', account: 'checking', kind: 'overdraft', amount: -35_00, months: [1, 4, 5, 9], day: 24 },
  { merchant: 'ATM fee', descriptor: 'NON-NETWORK ATM FEE', account: 'checking', kind: 'atm', amount: -3_50, months: [0, 2, 3, 5, 6, 8, 10, 11], day: 13 },
  { merchant: 'Monthly service fee', descriptor: 'MONTHLY MAINTENANCE FEE', account: 'checking', kind: 'monthlyMaintenance', amount: -12_00, months: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], day: 28 },
  { merchant: 'Late payment fee', descriptor: 'LATE PAYMENT CHARGE', account: 'card', kind: 'latePayment', amount: -39_00, months: [3, 8], day: 19 },
  { merchant: 'Foreign transaction fee', descriptor: 'FOREIGN TRANSACTION FEE', account: 'card', kind: 'foreignTransaction', amount: -4_20, months: [6, 7], day: 11 },
  { merchant: 'Interest charge', descriptor: 'INTEREST CHARGE ON PURCHASES', account: 'card', kind: 'interest', amount: -78_40, months: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], day: 26 },
  { merchant: 'Interest charge', descriptor: 'INTEREST CHARGE ON PURCHASES', account: 'card2', kind: 'interest', amount: -19_80, months: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], day: 26 },
]

export interface Dataset {
  accounts: Account[]
  statements: Statement[]
  transactions: Transaction[]
  emergencyFundTarget: Money | null
}

export function makeDataset(size: DatasetSize, now = new Date()): Dataset {
  if (size === 'empty') {
    return { accounts: [], statements: [], transactions: [], emergencyFundTarget: null }
  }
  const monthCount = size === 'thin' ? 1 : 12
  const slots = monthSlots(monthCount, now)
  const random = rng(0x5eed_1234)
  const today = now.getDate()

  const accounts: Account[] = ACCOUNT_SEEDS.map((seed) => ({
    id: uuid(`account:${seed.ref}`),
    name: seed.name,
    institution: seed.institution,
    type: seed.type,
    mask: seed.mask,
    currency: CURRENCY,
    currentBalance: money(seed.balance),
    availableBalance:
      seed.creditLimit !== undefined ? money(seed.creditLimit + seed.balance) : money(seed.balance),
    creditLimit: seed.creditLimit !== undefined ? money(seed.creditLimit) : undefined,
    aprBasisPoints: seed.aprBasisPoints ?? null,
    minimumPayment: seed.minimumPayment !== undefined ? money(seed.minimumPayment) : undefined,
    statementDayOfMonth: 1,
    coverage: {
      from: `${slots[0].key}-01`,
      to: isoDate(slots[slots.length - 1].year, slots[slots.length - 1].month, today),
      // One real gap so the dashboard `partial` state has something to say.
      gaps: size === 'full' && seed.ref === 'card2' ? [{ from: `${slots[4].key}-01`, to: `${slots[4].key}-28` }] : [],
    },
    transactionCount: 0,
    statementCount: slots.length,
    isDebt: seed.type === 'creditCard' || seed.type === 'loan',
    createdAt: `${slots[0].key}-01T09:00:00Z`,
    updatedAt: new Date(now).toISOString(),
  }))

  const accountByRef = new Map(ACCOUNT_SEEDS.map((seed, i) => [seed.ref, accounts[i]]))
  const idOf = (ref: string) => accountByRef.get(ref)!.id

  const transactions: Transaction[] = []
  const statements: Statement[] = []

  /** The current month is only covered up to today. */
  const inRange = (day: number, slot: MonthSlot) => slot.complete || day <= today

  slots.forEach((slot, monthIndex) => {
    const statementFor = (ref: string) => {
      const id = uuid(`statement:${ref}:${slot.key}`)
      const account = accountByRef.get(ref)!
      statements.push({
        id,
        accountId: account.id,
        fileName: `${(account.institution ?? account.name).toLowerCase().replace(/\W+/g, '-')}-${slot.key}.pdf`,
        fileSizeBytes: 148_000 + Math.floor(random() * 90_000),
        format: 'pdf',
        status: 'imported',
        failureReason: null,
        periodStart: `${slot.key}-01`,
        periodEnd: isoDate(slot.year, slot.month, daysIn(slot.year, slot.month)),
        openingBalance: money(0),
        closingBalance: account.currentBalance ?? money(0),
        detected: {
          institution: account.institution,
          accountType: account.type,
          mask: account.mask,
          currency: CURRENCY,
          confidence: 0.97,
        },
        transactionCount: 0,
        skippedRowCount: 0,
        duplicateCount: 0,
        balanceReconciles: true,
        uploadedAt: `${slot.key}-02T10:12:00Z`,
        importedAt: `${slot.key}-02T10:12:30Z`,
      })
      return id
    }
    const statementIds = new Map(ACCOUNT_SEEDS.map((s) => [s.ref, statementFor(s.ref)]))

    const add = (
      ref: string,
      day: number,
      merchant: string,
      descriptor: string,
      category: CategoryId,
      amount: number,
      extra: Partial<Transaction> = {},
    ) => {
      if (!inRange(day, slot)) return null
      const date = isoDate(slot.year, slot.month, day)
      const id = uuid(`tx:${ref}:${date}:${merchant}:${amount}:${transactions.length}`)
      transactions.push({
        id,
        accountId: idOf(ref),
        statementId: statementIds.get(ref) ?? null,
        date,
        postedDate: date,
        amount: money(amount),
        description: descriptor,
        merchant,
        merchantKey: merchantKey(merchant),
        category,
        categorySource: 'rule',
        categoryConfidence: 0.92,
        direction: amount >= 0 ? 'inflow' : 'outflow',
        isRecurring: false,
        recurringSeriesId: null,
        isFee: false,
        feeKind: null,
        isTransfer: false,
        transferPairId: null,
        isDuplicateOf: null,
        notes: null,
        createdAt: `${date}T12:00:00Z`,
        updatedAt: `${date}T12:00:00Z`,
        ...extra,
      })
      return id
    }

    // Salary, twice a month, with a small raise partway through the year.
    const pay = monthIndex >= 7 ? 4_412_00 : 4_190_00
    add('checking', 1, 'Northwind Labs', 'NORTHWIND LABS DIRECT DEP', 'income', pay)
    add('checking', 16, 'Northwind Labs', 'NORTHWIND LABS DIRECT DEP', 'income', pay)

    for (const seed of RECURRING_SEEDS) {
      const every = seed.everyMonths ?? 1
      if (monthIndex % every !== (every === 12 ? 2 : 0)) continue
      const amount =
        seed.priceChange && monthIndex >= seed.priceChange.atMonthIndex ? seed.priceChange.amount : seed.amount
      add(seed.account, seed.day, seed.merchant, seed.descriptor, seed.category, amount, {
        isRecurring: true,
        recurringSeriesId: uuid(`series:${merchantKey(seed.merchant)}`),
      })
    }

    for (const seed of VARIABLE_SEEDS) {
      const [minCount, maxCount] = seed.perMonth
      const count = minCount + Math.floor(random() * (maxCount - minCount + 1))
      for (let i = 0; i < count; i++) {
        const [low, high] = seed.amount
        const amount = Math.round(high + random() * (low - high))
        const day = 2 + Math.floor(random() * 26)
        add(seed.account, day, seed.merchant, seed.descriptor, seed.category, amount)
      }
    }

    for (const seed of FEE_SEEDS) {
      if (!seed.months.includes(monthIndex)) continue
      add(seed.account, seed.day, seed.merchant, seed.descriptor, 'fees', seed.amount, {
        isFee: true,
        feeKind: seed.kind,
      })
    }

    // Savings contribution: paired transfer between the user's own accounts.
    const savedAmount = monthIndex % 4 === 2 ? 120_00 : 320_00
    const savingsPair = uuid(`pair:savings:${slot.key}`)
    add('checking', 17, 'Emergency Savings', 'TRANSFER TO SAVINGS 3390', 'savingsTransfer', -savedAmount, {
      isTransfer: true,
      transferPairId: savingsPair,
    })
    add('savings', 17, 'Everyday Checking', 'TRANSFER FROM CHECKING 1042', 'transfer', savedAmount, {
      isTransfer: true,
      transferPairId: savingsPair,
    })

    // Credit card payments, also paired.
    for (const [ref, amount] of [
      ['card', 260_00],
      ['card2', 90_00],
    ] as const) {
      const pair = uuid(`pair:${ref}:${slot.key}`)
      add('checking', 20, accountByRef.get(ref)!.name, `PAYMENT TO ${ref.toUpperCase()}`, 'debtPayment', -amount, {
        isTransfer: true,
        transferPairId: pair,
      })
      add(ref, 20, 'Payment received', 'AUTOPAY PAYMENT THANK YOU', 'transfer', amount, {
        isTransfer: true,
        transferPairId: pair,
      })
    }
  })

  transactions.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : a.id < b.id ? 1 : -1))

  for (const account of accounts) {
    account.transactionCount = transactions.filter((t) => t.accountId === account.id).length
  }
  for (const statement of statements) {
    statement.transactionCount = transactions.filter((t) => t.statementId === statement.id).length
  }

  return {
    accounts,
    statements,
    transactions,
    emergencyFundTarget: size === 'full' ? money(12_000_00) : null,
  }
}

export { money, merchantKey, monthSlots, isoDate, daysIn, rng }
